(() => {
  if (customElements.get('reise-hero-slider')) return;

  class ReiseHeroSlider extends HTMLElement {
    connectedCallback() {
      this.initialize();
    }

    initialize() {
      if (this.controller || !this.isConnected) return;
      this.track = this.querySelector('[data-hero-track]');
      this.slides = [...(this.track?.children || [])].filter(slide => slide.matches('[data-hero-slide]'));
      if (!this.track || !this.slides.length) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.controls = this.querySelector('[data-hero-controls]');
      this.dots = [...this.querySelectorAll('[data-hero-dot]')];
      this.arrows = [...this.querySelectorAll('[data-hero-prev], [data-hero-next]')];
      this.status = this.querySelector('[data-hero-status]');
      this.autoplayButton = this.querySelector('[data-hero-autoplay-toggle]');
      this.videoButton = this.querySelector('[data-hero-video-toggle]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 767px)');
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplay = this.autoplayEnabled && !this.motion.matches;
      this.explicitAutoplay = false;
      const interval = Number(this.dataset.interval);
      this.interval = Number.isFinite(interval) && interval > 0 ? interval : 5000;
      this.hovered = false;
      this.focused = this.contains(document.activeElement);
      this.inViewport = false;
      this.editorSelected = false;
      this.videoUserPaused = false;
      this.explicitVideoPlay = false;
      this.targetIndex = null;
      this.announceIndex = null;
      this.announceScroll = false;
      this.trackWidth = 0;
      this.index = -1;

      this.addEventListener('click', this.onClick, options);
      this.addEventListener('keydown', this.onKeydown, options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.update();
      }, options);
      this.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.update();
      }, options);
      this.addEventListener('focusin', () => {
        this.focused = true;
        this.update();
      }, options);
      this.addEventListener('focusout', () => queueMicrotask(() => {
        if (!this.controller) return;
        this.focused = this.contains(document.activeElement);
        this.update();
      }), options);
      this.track.addEventListener('scroll', this.requestMeasure, { passive: true, ...options });
      this.track.addEventListener('pointerdown', this.onInteraction, { passive: true, ...options });
      this.track.addEventListener('wheel', event => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.onInteraction();
      }, { passive: true, ...options });
      document.addEventListener('visibilitychange', this.update, options);
      this.mobile.addEventListener('change', this.update, options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) {
          this.explicitVideoPlay = false;
          this.explicitAutoplay = false;
          this.autoplay = false;
        }
        this.update();
      }, options);
      this.addEventListener('shopify:block:select', this.onBlockSelect, options);
      this.addEventListener('shopify:block:deselect', () => {
        this.editorSelected = false;
        this.update();
      }, options);

      this.viewportObserver = new IntersectionObserver(entries => {
        this.inViewport = entries[0].isIntersecting;
        this.update();
      });
      this.viewportObserver.observe(this);
      this.resizeObserver = new ResizeObserver(() => {
        const width = this.track.clientWidth;
        if (!width || width === this.trackWidth) return;
        this.trackWidth = width;
        this.navigate(this.index, false, true);
      });
      this.resizeObserver.observe(this.track);
      this.activate(this.nearestIndex());
    }

    nearestIndex() {
      if (!this.track.clientWidth) return Math.max(0, this.index);
      const rect = this.track.getBoundingClientRect();
      const center = rect.left + this.track.clientLeft + this.track.clientWidth / 2;
      let nearest = 0;
      let distance = Infinity;
      this.slides.forEach((slide, index) => {
        const slideRect = slide.getBoundingClientRect();
        const nextDistance = Math.abs(slideRect.left + slideRect.width / 2 - center);
        if (nextDistance < distance) {
          nearest = index;
          distance = nextDistance;
        }
      });
      return nearest;
    }

    requestMeasure = () => {
      if (this.frame || !this.controller) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.activate(this.nearestIndex());
      });
    };

    activate(index) {
      if (this.index !== index) {
        this.index = index;
        this.explicitVideoPlay = false;
        this.dataset.activeIndex = String(index);
        this.slides.forEach((slide, position) => {
          const active = position === index;
          slide.inert = !active;
          slide.setAttribute('aria-hidden', String(!active));
          slide.classList.toggle('is-active', active);
        });
        if (this.announceScroll && this.status) {
          this.status.textContent = this.slides[index].getAttribute('aria-label') || this.counterText();
        }
      }
      if (this.targetIndex === index) this.targetIndex = null;
      if (this.announceIndex === index) {
        if (this.status) this.status.textContent = this.slides[index].getAttribute('aria-label') || this.counterText();
        this.announceIndex = null;
      }
      this.update();
    }

    navigate(index, manual = false, instant = false) {
      if (!this.controller || !this.slides.length) return;
      index = (index + this.slides.length) % this.slides.length;
      if (manual && this.autoplayButton) this.autoplay = false;
      this.targetIndex = index;
      this.announceScroll = false;
      this.announceIndex = manual ? index : null;
      const slideRect = this.slides[index].getBoundingClientRect();
      const trackRect = this.track.getBoundingClientRect();
      const left = this.track.scrollLeft + slideRect.left - trackRect.left - this.track.clientLeft;
      const smooth = !instant && !this.motion.matches;
      this.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
      this.requestMeasure();
      this.update();
    }

    onInteraction = () => {
      if (this.autoplayButton) this.autoplay = false;
      this.targetIndex = null;
      this.announceIndex = null;
      this.announceScroll = true;
      this.update();
    };

    onClick = event => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.matches('[data-hero-dot], [data-hero-prev], [data-hero-next]')) {
        const index = button.matches('[data-hero-dot]') ? Number(button.dataset.heroDot)
          : (this.targetIndex ?? this.index) + (button.hasAttribute('data-hero-prev') ? -1 : 1);
        this.navigate(index, true);
        if (!this.autoplayButton && event.detail > 0) {
          button.blur();
          this.focused = false;
          this.update();
        }
      } else if (button === this.autoplayButton && this.autoplayEnabled) {
        this.autoplay = !this.autoplay;
        this.explicitAutoplay = this.autoplay;
        this.hovered = false;
        this.focused = false;
        this.update();
      } else if (button === this.videoButton) {
        const playing = this.playPending || (this.video && !this.video.paused);
        this.videoUserPaused = !!playing;
        this.explicitVideoPlay = !playing;
        this.playFailed = false;
        this.autoplay = false;
        this.update();
      }
    };

    onKeydown = event => {
      if (event.target !== this.track || event.altKey || event.ctrlKey || event.metaKey) return;
      let index = this.targetIndex ?? this.index;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      if (event.key === 'ArrowRight') index += rtl ? -1 : 1;
      else if (event.key === 'ArrowLeft') index += rtl ? 1 : -1;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.slides.length - 1;
      else return;
      event.preventDefault();
      this.navigate(index, true);
    };

    onBlockSelect = event => {
      const slide = (event.target instanceof Element && event.target.closest('[data-hero-slide]')) || this.slides.find(item => item.dataset.blockId === String(event.detail?.blockId));
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      this.editorSelected = true;
      this.navigate(index, false, true);
    };

    videoTemplate() {
      const slide = this.slides[this.index];
      return (this.mobile.matches && slide.querySelector('template[data-hero-video-mobile]')) || slide.querySelector('template[data-hero-video-desktop]');
    }

    canPlayVideo() {
      return this.inViewport && !document.hidden && !this.videoUserPaused && (!this.motion.matches || this.explicitVideoPlay);
    }

    syncVideo() {
      const template = this.videoTemplate();
      const media = this.slides[this.index].querySelector('[data-hero-media]');
      if (!template || !media || !this.inViewport || document.hidden) {
        this.releaseVideo();
        return;
      }
      if (this.videoTemplateNode !== template || this.video?.parentElement !== media) this.releaseVideo();
      if (!this.video && this.canPlayVideo()) {
        const video = template.content.querySelector('video')?.cloneNode(true);
        if (!video) return;
        this.video = video;
        this.videoTemplateNode = template;
        this.playFailed = false;
        this.mediaController = new AbortController();
        const options = { signal: this.mediaController.signal };
        video.removeAttribute('autoplay');
        video.autoplay = false;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = true;
        video.preload = 'none';
        video.addEventListener('playing', () => {
          if (this.video !== video || !this.canPlayVideo()) {
            video.pause();
            return;
          }
          this.playPending = false;
          video.classList.add('is-playing');
          this.updateControls();
        }, options);
        video.addEventListener('pause', () => this.updateControls(), options);
        video.addEventListener('error', () => {
          this.playAttempt = (this.playAttempt || 0) + 1;
          this.playFailed = true;
          this.playPending = false;
          video.classList.remove('is-playing');
          video.pause();
          this.updateControls();
        }, options);
        media.append(video);
      }
      if (!this.video) return;
      if (!this.canPlayVideo()) {
        if (this.playPending || !this.video.paused) this.playAttempt = (this.playAttempt || 0) + 1;
        this.playPending = false;
        this.video.pause();
      } else if (this.video.paused && !this.playPending && !this.playFailed) {
        const video = this.video;
        const attempt = this.playAttempt = (this.playAttempt || 0) + 1;
        this.playPending = true;
        const failed = () => {
          if (this.video !== video || this.playAttempt !== attempt) return;
          this.playPending = false;
          this.playFailed = this.canPlayVideo();
          this.updateControls();
        };
        try {
          Promise.resolve(video.play()).then(() => {
            if (this.video !== video || this.playAttempt !== attempt) return;
            this.playPending = false;
            this.updateControls();
          }).catch(failed);
        } catch {
          failed();
        }
      }
    }

    releaseVideo() {
      this.playAttempt = (this.playAttempt || 0) + 1;
      this.mediaController?.abort();
      this.mediaController = null;
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        this.video.load();
        this.video.remove();
      }
      this.video = null;
      this.videoTemplateNode = null;
      this.playPending = false;
    }

    counterText() {
      return `${this.index + 1} / ${this.slides.length}`;
    }

    updateControls() {
      const hasVideo = !!this.videoTemplate();
      if (this.controls) this.controls.hidden = this.slides.length < 2 && !hasVideo;
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.index)));
      this.arrows.forEach(arrow => { arrow.hidden = this.slides.length < 2; });
      const label = (button, playing) => {
        if (!button) return;
        const text = playing ? button.dataset.labelPause : button.dataset.labelPlay;
        if (text) {
          const labelNode = button.querySelector('[data-hero-playback-label]') || button;
          if (labelNode.textContent !== text) labelNode.textContent = text;
          if (button.getAttribute('aria-label') !== text) button.setAttribute('aria-label', text);
          button.dataset.playing = String(playing);
        }
      };
      if (this.autoplayButton) this.autoplayButton.hidden = this.slides.length < 2 || !this.autoplayEnabled;
      if (this.videoButton) this.videoButton.hidden = !hasVideo;
      label(this.autoplayButton, this.autoplay);
      label(this.videoButton, this.playPending || (this.video && !this.video.paused));
    }

    update = () => {
      if (!this.controller || this.index < 0) return;
      this.syncVideo();
      this.updateControls();
      clearTimeout(this.rotationTimer);
      if (!this.autoplay || this.slides.length < 2 || this.hovered || this.focused || this.editorSelected || (this.motion.matches && !this.explicitAutoplay) || document.hidden || !this.inViewport) return;
      this.rotationTimer = setTimeout(() => {
        this.rotationTimer = 0;
        this.navigate(this.index + 1);
      }, this.interval);
    };

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      if (!this.controller) return;
      this.controller.abort();
      this.controller = null;
      this.viewportObserver?.disconnect();
      this.resizeObserver?.disconnect();
      clearTimeout(this.rotationTimer);
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.releaseVideo();
      this.slides.forEach(slide => {
        slide.inert = false;
        slide.removeAttribute('aria-hidden');
        slide.classList.remove('is-active');
      });
      delete this.dataset.activeIndex;
    }
  }

  customElements.define('reise-hero-slider', ReiseHeroSlider);
  const sectionSliders = (event, method) => {
    const root = event.target;
    if (!(root instanceof Element)) return;
    if (root.matches('reise-hero-slider')) root[method]();
    root.querySelectorAll('reise-hero-slider').forEach(slider => slider[method]());
  };
  document.addEventListener('shopify:section:load', event => sectionSliders(event, 'initialize'));
  document.addEventListener('shopify:section:unload', event => sectionSliders(event, 'destroy'));
})();
